/**
 * assets/free-gift-control.js — Free Gift with Purchase (A/B test CONTROL cleanup).
 *
 * Loaded only for the Control experience (same injection mechanism as the
 * Variation's free-gift.js, just the opposite bucket). Single responsibility:
 * if the free-gift variant is present in the cart, remove it. Never adds,
 * never changes any other line item.
 *
 * UI strategy: reuses the theme's OWN removal path —
 * CartItems/CartDrawerItems.updateQuantity(line, 0) — the exact method
 * CartRemoveButton calls for a normal remove click. That method already
 * correctly re-renders the cart drawer and cart-icon-bubble, so there is no
 * custom section-parsing logic here to get wrong.
 * 
 * IMPORTANT — concurrency: updateQuantity() is fire-and-forget from our
 * side; it kicks off the theme's own /cart/change.js request but doesn't
 * hand us a promise we can await. Removal is therefore done by LINE NUMBER
 * (position in the cart), which is only correct at the instant it's sent.
 * If a second removal attempt fires before the first one has actually
 * reached Shopify (e.g. because our own trigger point resets its "busy"
 * state too early), both requests can carry the SAME stale line number.
 * Shopify processes them in order: the first correctly removes the gift,
 * which shifts every later line up by one — so the second request, still
 * holding the old line number, now lands on whatever shifted into that
 * position and deletes THAT instead. `mutationCount` below exists purely to
 * prevent that: only one cart-mutation attempt from this script is ever in
 * flight, and nothing re-checks the cart until the prior one has fully
 * settled (not just been sent).
 *
 * Companion to assets/free-gift.js — that file is NOT modified by this one,
 * and this file never runs in the same bucket as it (enforced by whatever
 * injects this script only into Control, plus a same-page safety check below).
 *
 * Requires window.freeGiftConfig = { giftVariantId } set via Liquid.
 */
(function () {
        var VARIATION_BODY_CLASS = 'free-gift-variation-active';
  function isVariationActive() {
    return document.documentElement.classList.contains(VARIATION_BODY_CLASS) ||
      (document.body && document.body.classList.contains(VARIATION_BODY_CLASS));
  }
 
  // Bail immediately if Variation has already marked the page by the time
  // this script itself parses.
  if (isVariationActive()) return;
  function init() {
    'use strict';

    // Safety net: if the Variation script is somehow also active on this
    // page, do nothing. (window.FreeGift is only set by free-gift.js after
    // it successfully initializes.)
    if (window.FreeGift) return;

    var CONFIG = window.freeGiftConfig || {};
    var GIFT_VARIANT_ID = Number(CONFIG.giftVariantId);
    if (!GIFT_VARIANT_ID) return; // nothing to clean up without knowing the variant id

    var originalFetch = window.fetch.bind(window); // captured once; our own reads never re-enter the patched fetch
    var routes = window.routes || {};
    var CART_URL = (routes.cart_url ? routes.cart_url.replace(/\.js$/, '') : '/cart') + '.js';
    var UPDATE_URL = routes.cart_update_url || '/cart/update.js';

    // Counter, not a boolean: two overlapping mutations (e.g. a user firing
    // two adds in quick succession) must both finish before we trust the
    // cart's line order again.
    var mutationCount = 0;

    function getCart() {
      return originalFetch(CART_URL, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
    }

    // Returns the gift's 1-based cart line, or 0 if it's not in the cart.
    function findGiftLine(cart) {
      if (!cart || !Array.isArray(cart.items)) return 0;
      for (var i = 0; i < cart.items.length; i++) {
        if (Number(cart.items[i].variant_id) === GIFT_VARIANT_ID) return i + 1;
      }
      return 0;
    }

    // Reuse the theme's own removal path. cart-drawer-items is present in
    // the header on virtually every page and, per its getSectionsToRender(),
    // updates both the drawer and cart-icon-bubble in one call — the same
    // update CartRemoveButton triggers for a normal remove click.
    function removeViaTheme(line) {
      var target = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (!target || typeof target.updateQuantity !== 'function') return false;
      target.updateQuantity(String(line), 0); // fires the theme's own /cart/change.js — picked up by the interceptor below
      return true;
    }

    // Only ever called when mutationCount === 0, i.e. nothing else is
    // touching the cart right now, so `line` is trustworthy at the moment
    // it's used.
    function removeGift(line) {
      if (mutationCount > 0) return; // something started since this line was computed — let that settle, sync() will re-check after
      if (!removeViaTheme(line)) {
        // Fallback for the rare page where neither cart component is
        // present in the DOM: remove directly via the Cart API. This bypasses
        // the theme entirely, so we account for it in mutationCount by hand.
        mutationCount++;
        var updates = {};
        updates[GIFT_VARIANT_ID] = 0;
        originalFetch(UPDATE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).catch(function (e) { console.error('[free-gift-control] fallback removal failed', e); })
          .finally(function () { mutationCount = Math.max(0, mutationCount - 1); if (mutationCount === 0) sync(); });
      }
      // If removeViaTheme() succeeded, its own fetch call is already being
      // tracked by the interceptor below (it goes through window.fetch), so
      // no manual bookkeeping is needed on this branch.
    }

    // The single source of truth: only runs when nothing else is in
    // flight, fetches a FRESH cart, and only then decides whether to remove.
    function sync() {
      if (mutationCount > 0) return;
      getCart().then(function (cart) {
        if (mutationCount > 0) return; // something started while this fetch was in flight — bail, it'll re-trigger sync() when it settles
        var line = findGiftLine(cart);
        if (line) removeGift(line);
      }).catch(function (e) { console.error('[free-gift-control] sync failed', e); });
    }

    // Watch every cart-mutating AJAX call (add/change/update), from us OR
    // the theme OR anything else on the page. Every request passes through
    // untouched — this only counts them, and re-checks the cart once the
    // LAST one in flight has fully settled.
    window.fetch = function (input, init) {
      init = init || {};
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init.method || 'GET').toUpperCase();
      var isMutation = method === 'POST' && /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);

      if (!isMutation) return originalFetch(input, init);

      mutationCount++;
      return originalFetch(input, init).finally(function () {
        mutationCount = Math.max(0, mutationCount - 1);
        if (mutationCount === 0) sync(); // re-check with a clean, fully-settled cart
      });
    };

    // Extra safety net for cart updates that originate elsewhere.
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () { sync(); });
    }

    // Run once on load — catches a gift left over from a prior Variation visit.
    sync();

    window.FreeGiftControl = { getCart: getCart, forceSync: sync };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
